import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Developers from "../components/Developers";
import {
  ArrowTrendingUpIcon,
  BellAlertIcon,
  BoltIcon,
  ChartBarSquareIcon,
  CheckCircleIcon,
  ChatBubbleBottomCenterTextIcon,
  CursorArrowRaysIcon,
  InboxArrowDownIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const benefits = [
  {
    title: "Fast delivery",
    description: "Send time-sensitive alerts, OTPs, and updates through a reliable SMS channel.",
    icon: BoltIcon,
  },
  {
    title: "Bulk reach",
    description: "Broadcast messages to large audiences for campaigns, announcements, and reminders.",
    icon: UserGroupIcon,
  },
  {
    title: "Personalized content",
    description: "Use names, balances, order IDs, and customer details to make every SMS relevant.",
    icon: CursorArrowRaysIcon,
  },
  {
    title: "Delivery visibility",
    description: "Track queued, sent, delivered, failed, and expired messages from your workflow.",
    icon: ChartBarSquareIcon,
  },
];

const useCases = [
  ["OTP and verification", "Send one-time passwords for signups, logins, payments, and account recovery."],
  ["Marketing campaigns", "Share launches, discounts, events, and retention offers at scale."],
  ["Operational alerts", "Notify customers about payments, orders, deliveries, outages, and appointments."],
  ["Customer engagement", "Run surveys, reminders, feedback requests, and loyalty messages."],
];

const messages = [
  ["WhistlePay", "Your payment of GHS 240.00 was received. Ref: WP-24019", "Delivered"],
  ["ShopPulse", "Ama, your order is ready for pickup at Osu branch.", "Sent"],
  ["SecureID", "Your verification code is 482190. It expires in 5 minutes.", "Delivered"],
];

function SMSMessaging() {
  return (
    <div className="bg-slate-50">
      <Navbar />

      <section className="relative overflow-hidden bg-[#071d33] text-white">
        <div className="absolute inset-x-0 bottom-0 h-28 bg-slate-50" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-[85rem] gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">SMS messaging</p>
            <h1 className="mt-5 text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              Send messages customers actually see.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Whistle Pulse helps businesses send single, bulk, personalized, and OTP messages across Africa with a
              simple dashboard and developer-friendly APIs.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="https://dashboard.whistlepulse.com/auth/register"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-950/30 transition hover:bg-blue-400"
              >
                Start sending
              </a>
              <a
                href="/developers"
                className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                View API docs
              </a>
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-xl">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 shadow-2xl shadow-black/30 backdrop-blur">
              <div className="rounded-xl bg-[#041629] p-5">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div>
                    <p className="text-sm text-blue-200">Campaign performance</p>
                    <p className="mt-1 text-3xl font-bold">128,420 SMS</p>
                  </div>
                  <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-sm font-semibold text-emerald-200">
                    live
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  {[
                    ["Delivered", "98.4%"],
                    ["Avg. latency", "4 sec"],
                    ["Recipients", "42.8K"],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-white/8 p-4">
                      <p className="text-xs text-blue-200">{label}</p>
                      <p className="mt-2 text-xl font-bold text-white">{value}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 space-y-3">
                  {messages.map(([sender, message, status]) => (
                    <div key={`${sender}-${message}`} className="rounded-xl border border-white/10 bg-[#071d33] p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-semibold text-white">{sender}</p>
                        <p className={`text-xs ${status === "Delivered" ? "text-emerald-200" : "text-blue-200"}`}>
                          {status}
                        </p>
                      </div>
                      <p className="mt-3 text-sm leading-6 text-blue-100">{message}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {benefits.map((benefit) => (
            <div key={benefit.title} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <benefit.icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h2 className="mt-5 text-lg font-bold text-slate-950">{benefit.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto grid max-w-[85rem] gap-10 px-4 pb-16 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-600">Common workflows</p>
          <h2 className="mt-4 text-3xl font-bold text-slate-950 sm:text-4xl">
            Use SMS for moments that need attention.
          </h2>
          <p className="mt-5 text-base leading-7 text-slate-600">
            From verification to customer retention, SMS gives teams a direct channel for high-priority communication.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {useCases.map(([title, description]) => (
            <div key={title} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="mt-0.5 h-6 w-6 shrink-0 text-blue-600" aria-hidden="true" />
                <div>
                  <h3 className="font-semibold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-[85rem] gap-10 px-4 py-16 sm:px-6 lg:grid-cols-3 lg:px-8">
          {[
            [InboxArrowDownIcon, "Import recipients", "Upload contacts, segment audiences, or trigger messages from your system."],
            [ChatBubbleBottomCenterTextIcon, "Compose and personalize", "Send one shared message or tailor content for each recipient."],
            [BellAlertIcon, "Deliver and monitor", "Track delivery states, retry safely, and review campaign performance."],
          ].map(([Icon, title, description], index) => (
            <div key={title} className="relative rounded-lg border border-slate-200 p-6">
              <span className="absolute right-5 top-5 font-mono text-sm font-bold text-slate-200">0{index + 1}</span>
              <Icon className="h-8 w-8 text-blue-600" aria-hidden="true" />
              <h3 className="mt-5 text-xl font-bold text-slate-950">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[85rem] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 rounded-2xl bg-[#071d33] p-6 text-white sm:p-8 lg:grid-cols-[0.75fr_1.25fr] lg:items-center">
          <div>
            <ShieldCheckIcon className="h-9 w-9 text-blue-300" aria-hidden="true" />
            <h2 className="mt-5 text-2xl font-bold">Made for trusted communication</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              [ArrowTrendingUpIcon, "Scale campaigns without losing control."],
              [CheckCircleIcon, "Support transactional and marketing use cases."],
            ].map(([Icon, text]) => (
              <div key={text} className="flex items-start gap-3 rounded-lg bg-white/5 p-4">
                <Icon className="mt-0.5 h-6 w-6 shrink-0 text-blue-300" aria-hidden="true" />
                <p className="text-sm leading-6 text-blue-50">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Developers />
      <Footer />
    </div>
  );
}

export default SMSMessaging;
